import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';
import { demoDeliveries, demoDeliveryFiles } from '@/data/demoOverlay';
// TUS-1 — Route delivery photo uploads through the same TUS
// helper Galleries uses, so files above Supabase's 100MB
// single-PUT cap actually land instead of silently 413'ing.
import { resumableUpload } from '@/lib/resumableUpload';

export interface Delivery {
  id: string;
  user_id: string;
  project_id: string | null;
  title: string;
  client_name: string;
  client_email: string | null;
  slug: string;
  // Plaintext password never leaves the DB after wave3 — the
  // migration hashes it on write and CHECK-constrains the column
  // to NULL. Studio-side reads use `password_hash` as a boolean-ish
  // "is a password set?" signal (never verified in the browser).
  // Optional so a stale auto-gen types.ts (pre types-regen) doesn't
  // break every `select * from deliveries` call; the DB column
  // exists after 20260720_wave3_delivery_security.sql lands.
  password_hash?: string | null;
  expires_at: string | null;
  created_at: string;
  view_count: number;
  greeting_text: string | null;
  description: string | null;
  cover_image_url: string | null;
  accent_color: string | null;
  gallery_id: string | null;
}

// Public-facing types used by PublicDelivery.tsx after Wave 3.
// The client never sees the password_hash — only these shapes.
export interface PublicDeliveryMeta {
  not_found?: boolean;
  title?: string;
  greeting_text?: string | null;
  description?: string | null;
  cover_image_url?: string | null;
  accent_color?: string | null;
  needs_password?: boolean;
  expired?: boolean;
}

export interface UnlockedDelivery {
  unlocked: true;
  delivery: Omit<Delivery, 'password_hash' | 'user_id' | 'project_id' | 'created_at' | 'client_email'>;
  files: DeliveryFile[];
  /**
   * Wave 4 — the initial page of timecode-anchored video comments
   * for every video in this delivery, hydrated inside the same
   * unlock RPC so the viewer doesn't need a second anon round-trip
   * to render threads. Realtime + follow-up mutations flow through
   * `useDeliveryVideoComments` hooks; this array is just the seed.
   * Older Wave 3 RPC deployments won't return this key — treat it
   * as optional and default to `[]`.
   */
  video_comments?: Array<{
    id: string;
    delivery_file_id: string;
    delivery_id: string;
    timecode_seconds: number | null;
    body: string;
    author_name: string;
    author_email: string | null;
    parent_id: string | null;
    resolved: boolean;
    resolved_at: string | null;
    resolved_by: string | null;
    created_at: string;
  }>;
}

export type UnlockResult =
  | UnlockedDelivery
  | { unlocked: false; error: 'not_found' | 'expired' | 'wrong_password' | 'rate_limited' };

export interface DeliveryFile {
  id: string;
  delivery_id: string;
  file_name: string;
  file_type: string;
  file_size: number | null;
  storage_path: string | null;
  mux_upload_id: string | null;
  mux_asset_id: string | null;
  mux_playback_id: string | null;
  mux_status: string | null;
  duration_seconds: number | null;
  /**
   * PLANS-3 — plan-correct mp4 download URL written by mux-webhook
   * (capped-1080p.mp4 on Orbit, highest.mp4 up to 4K on Business).
   * NULL for pre-PLANS-3 rows — use `deliveryFileMp4Url` below, which
   * falls back to capped-1080p.mp4 (always right for old assets).
   */
  mux_mp4_url?: string | null;
  sort_order: number;
  created_at: string;
  /**
   * Wave 4 — nullable grouping key. Two video files sharing the
   * same `compare_pair_id` are rendered as a side-by-side compare
   * player in the public delivery viewer. Studios set this via the
   * "Pair with…" affordance in DeliveryDetail. Null (default) means
   * the file renders solo.
   */
  compare_pair_id?: string | null;
}

// GAL-4 — exported so services/galleries.ts can mint the same slug
// shape for a gallery's auto-created companion delivery.
export function generateSlug(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// GAL-4 — sentinel description for the auto-created delivery that
// backs a gallery's videos. Gallery video uploads need a
// `deliveries` row (Mux pipeline is keyed on delivery_id), so
// `ensureGalleryDelivery` in services/galleries.ts creates one per
// gallery with this exact description. The Deliveries list filters
// these rows out (see useDeliveries) so studios never see the
// plumbing. The text is honest prose in case a client ever lands
// on the raw /delivery/:slug page.
export const GALLERY_COMPANION_DESCRIPTION =
  'Auto-managed by Orbit — this delivery holds the video files for a gallery.';

export const isGalleryCompanionDelivery = (
  d: Pick<Delivery, 'gallery_id' | 'description'>,
): boolean =>
  !!d.gallery_id && d.description === GALLERY_COMPANION_DESCRIPTION;

export const useDeliveries = () => {
  const { user } = useAuth();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useQuery({
    queryKey: ['deliveries', user?.id, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      // Demo mode short-circuit. The shape of demoDeliveries
      // overlaps with the Delivery interface for the fields the
      // Deliveries grid actually reads (title, client_name,
      // cover_image_url, created_at). Cast through unknown so the
      // extra demo fields (thumbnails, file_count, etc.) don't
      // trip up the type checker — the grid ignores them anyway.
      if (isDemo) {
        const rows = demoStore.deliveries.list();
        return (rows.length > 0 ? rows : demoDeliveries) as unknown as Delivery[];
      }
      const { data, error } = await supabase
        .from('deliveries')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      // GAL-4 — hide gallery companion deliveries (auto-created rows
      // that only exist to hold a gallery's Mux videos). They're
      // managed from the gallery UI, not here.
      return (data as Delivery[]).filter((d) => !isGalleryCompanionDelivery(d));
    },
    enabled: isDemo || !!user,
  });
};

export const useDelivery = (id: string | undefined) => {
  const { user } = useAuth();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useQuery({
    queryKey: ['delivery', id, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (isDemo) {
        const rows = demoStore.deliveries.list();
        const source = (rows.length > 0 ? rows : demoDeliveries) as any[];
        return (source.find((d) => d.id === id) ?? null) as Delivery | null;
      }
      const { data, error } = await supabase
        .from('deliveries')
        .select('*')
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as Delivery;
    },
    enabled: (isDemo || !!user) && !!id,
  });
};

export const useDeliveryFiles = (deliveryId: string | undefined) => {
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useQuery({
    queryKey: ['delivery-files', deliveryId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (isDemo) {
        const rows = demoStore.deliveryFiles.list();
        const source = (rows.length > 0 ? rows : demoDeliveryFiles) as any[];
        return source
          .filter((f) => f.delivery_id === deliveryId)
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)) as DeliveryFile[];
      }
      const { data, error } = await supabase
        .from('delivery_files')
        .select('*')
        .eq('delivery_id', deliveryId!)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data as DeliveryFile[];
    },
    enabled: !!deliveryId,
  });
};

export const useUpdateDelivery = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // `password` is still accepted as an INPUT to update — the DB
    // trigger will bcrypt it into `password_hash` and clear the
    // plaintext field before the row is written. The Delivery
    // interface itself no longer carries plaintext.
    mutationFn: async ({ id, ...updates }: Partial<Delivery> & { id: string; password?: string | null }) => {
      const { data, error } = await supabase
        .from('deliveries')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as Delivery;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['delivery', data.id] });
    },
  });
};

export const useCreateDelivery = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      title: string;
      client_name: string;
      client_email?: string;
      project_id?: string;
      password?: string;
      expires_at?: string;
    }) => {
      const slug = generateSlug();
      const { data, error } = await supabase
        .from('deliveries')
        .insert({
          ...input,
          user_id: user!.id,
          slug,
          client_email: input.client_email || null,
          project_id: input.project_id || null,
          password: input.password || null,
          expires_at: input.expires_at || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as Delivery;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
    },
  });
};

export const useDeleteDelivery = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('deliveries').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
    },
  });
};

export const useDeleteDeliveryFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: DeliveryFile) => {
      // Delete from storage if photo
      if (file.file_type === 'photo' && file.storage_path) {
        await supabase.storage.from('delivery-photos').remove([file.storage_path]);
      }
      const { error } = await supabase.from('delivery_files').delete().eq('id', file.id);
      if (error) throw error;
      return file.delivery_id;
    },
    onSuccess: (deliveryId) => {
      queryClient.invalidateQueries({ queryKey: ['delivery-files', deliveryId] });
    },
  });
};

// Public queries (no auth) — Wave 3.
//
// Anon can no longer read the deliveries table directly. Two RPCs
// replace the previous direct select:
//
//   1. usePublicDeliveryMeta — always-safe metadata (title, cover,
//      needs_password, expired). Drives the "which screen do we
//      render?" decision in PublicDelivery.tsx.
//   2. useUnlockPublicDelivery — server-side password verification
//      that returns the full delivery + files on success. This is
//      also the sole path that yields delivery.id to the browser.
export const usePublicDeliveryMeta = (slug: string | undefined) => {
  return useQuery({
    queryKey: ['public-delivery-meta', slug],
    queryFn: async () => {
      // Cast through `any` — the generated types file is regenerated
      // out-of-band and doesn't yet know about these Wave 3 RPCs
      // (same pattern as get_public_org_by_slug in services/*).
      const { data, error } = await (supabase as any).rpc('get_public_delivery_meta', {
        p_slug: slug!,
      });
      if (error) throw error;
      return data as PublicDeliveryMeta;
    },
    enabled: !!slug,
  });
};

export const useUnlockPublicDelivery = () => {
  return useMutation({
    mutationFn: async ({ slug, password }: { slug: string; password: string | null }) => {
      const { data, error } = await (supabase as any).rpc('unlock_public_delivery', {
        p_slug: slug,
        p_password: password ?? '',
      });
      if (error) throw error;
      return data as UnlockResult;
    },
  });
};

// Live query for the files list, used AFTER unlock. The anon SELECT
// policy on `delivery_files` is gated on the parent delivery not
// being expired (see migration 20260720_wave3_delivery_security.sql),
// so a caller who has already unlocked via the RPC can subscribe to
// realtime Mux status updates on this query without leaking anything
// new. This is what powers "video is transcoding..." → "ready"
// without a full page refresh.
export const usePublicDeliveryFiles = (deliveryId: string | undefined) => {
  return useQuery({
    queryKey: ['public-delivery-files', deliveryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('delivery_files')
        .select('*')
        .eq('delivery_id', deliveryId!)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data as DeliveryFile[];
    },
    enabled: !!deliveryId,
  });
};

// Realtime subscription for delivery files (for instant mux status updates)
export const useDeliveryFilesRealtime = (deliveryId: string | undefined) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!deliveryId) return;
    const channel = supabase
      .channel(`delivery-files-${deliveryId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'delivery_files',
          filter: `delivery_id=eq.${deliveryId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['delivery-files', deliveryId] });
          queryClient.invalidateQueries({ queryKey: ['public-delivery-files', deliveryId] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [deliveryId, queryClient]);
};

// Get deliveries linked to a project
export const useDeliveriesByProject = (projectId: string | undefined) => {
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useQuery({
    queryKey: ['deliveries-by-project', projectId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      // DEMO-4 — this had no demo branch, so ProjectWorkspace under
      // Preview Studio sent `project_id = 'demo-p-1'` at a uuid column.
      // Postgres 22P02'd, react-query swallowed it into an empty array,
      // and the project's deliveries + the "next action" nudge that
      // counts them were quietly always zero.
      if (isDemo) {
        return demoStore.deliveries
          .list()
          .filter((d) => d.project_id === projectId) as unknown as Delivery[];
      }

      const { data, error } = await supabase
        .from('deliveries')
        .select('*')
        .eq('project_id', projectId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Delivery[];
    },
    enabled: !!projectId,
  });
};

// Public: get deliveries for a project (for portal use via anon).
// Wave 3 — routed through a SECURITY DEFINER RPC that returns only
// the display-safe subset (no password_hash, no client_email, no
// user_id). Shape includes `needs_password: boolean` so the tab can
// still show the "Protected" badge without ever seeing the hash.
export interface PublicDeliverySummary {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  cover_image_url: string | null;
  accent_color: string | null;
  expires_at: string | null;
  view_count: number;
  needs_password: boolean;
  created_at: string;
}

// SEC-1 lockdown — get_public_deliveries_by_project was dropped: a
// bare project UUID carried no credential yet returned the delivery
// slugs (which ARE the delivery credential). The portal token keys
// the replacement RPC; same row shape.
export const usePublicDeliveriesByToken = (token: string | undefined) => {
  return useQuery({
    queryKey: ['public-deliveries-by-token', token],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_public_portal_deliveries', {
        p_token: token!,
      });
      if (error) throw error;
      return (data as PublicDeliverySummary[]) ?? [];
    },
    enabled: !!token,
  });
};

// incrementViewCount was removed in Wave 3 — view_count is now
// bumped inside `unlock_public_delivery` on the server so a client
// cannot inflate the counter or track a delivery without unlocking.

export const uploadDeliveryPhoto = async (
  userId: string,
  deliveryId: string,
  file: File,
  // POL-1 — now reports a 0…1 fraction (was percent) so the caller
  // can compute bytesUploaded + speed against `file.size` in the
  // same shape as `uploadVideoToMux`. Callers that want percent
  // just multiply by 100.
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
  // POL-1 — fires the moment the TUS transfer completes and the
  // DB insert begins. Callers use this to flip the row into a
  // "Finalizing…" state so a huge upload's last few seconds don't
  // look frozen at 100 %.
  onFinalize?: () => void,
) => {
  const storagePath = `${userId}/${deliveryId}/${file.name}`;

  // TUS-1 — Was `supabase.storage.from('delivery-photos').upload(...)`,
  // which silently 413s on files above Supabase's 100MB per-request
  // cap. Deliveries carry RAWs and multi-GB video stills, so we now
  // route through the shared TUS helper Galleries already uses.
  // `upsert: true` preserves the previous behavior (re-uploading
  // the same file name overwrites the prior object, so a retry-on-
  // network-blip doesn't fail with "Object already exists").
  await resumableUpload({
    bucket: 'delivery-photos',
    path: storagePath,
    file,
    upsert: true,
    signal,
    onProgress: (fraction) => onProgress?.(fraction),
  });

  onFinalize?.();

  const { data: insertData, error: insertError } = await supabase
    .from('delivery_files')
    .insert({
      delivery_id: deliveryId,
      file_name: file.name,
      file_type: 'photo',
      file_size: file.size,
      storage_path: storagePath,
    })
    .select()
    .single();

  if (insertError) throw insertError;
  return insertData as DeliveryFile;
};

/**
 * PLANS-3 — the mp4 rendition name is plan-dependent (capped-1080p on
 * Orbit, highest.mp4 on Business), so prefer the webhook-derived URL
 * and only fall back to the historical capped-1080p path for rows
 * that predate the migration.
 */
export const deliveryFileMp4Url = (file: {
  mux_mp4_url?: string | null;
  mux_playback_id: string | null;
}): string | null => {
  if (file.mux_mp4_url) return file.mux_mp4_url;
  if (!file.mux_playback_id) return null;
  return `https://stream.mux.com/${file.mux_playback_id}/capped-1080p.mp4`;
};

/**
 * FIX-2 — supabase-js collapses every non-2xx edge-function response
 * into "Edge Function returned a non-2xx status code", which is what
 * the studio saw in a red toast when video upload failed. The real
 * reason (plan gate, expired session, missing Mux credentials, Mux
 * rejecting the request) is in the response body. Dig it out and turn
 * it into a sentence that says what happened and what to do.
 */
async function describeCreateMuxUploadError(error: unknown): Promise<string> {
  const context = (error as { context?: Response } | null)?.context;
  let payload: { error?: string; feature?: string } | null = null;
  let status: number | undefined;
  try {
    if (context && typeof context === 'object') {
      status = (context as Response).status;
      if (typeof (context as Response).clone === 'function') {
        payload = await (context as Response).clone().json();
      }
    }
  } catch {
    // Body wasn't JSON (or was already consumed) — fall through to the
    // status-code branches below.
  }

  const code = typeof payload?.error === 'string' ? payload.error : '';

  if (code === 'plan_locked') {
    return 'Video delivery isn’t part of your current plan. Upgrade to Orbit to send video to clients.';
  }
  if (status === 401) {
    return 'Your session expired before the upload started. Sign in again, then add the video.';
  }
  if (status === 403) {
    return 'You don’t have permission to add video to this delivery.';
  }
  if (status === 404) {
    return 'This delivery no longer exists. Reload the page and try again.';
  }
  if (/MUX_TOKEN/i.test(code)) {
    return 'Video uploads aren’t switched on for this workspace yet — the Mux API keys are missing on the server.';
  }
  if (/^Mux API error/i.test(code)) {
    return `Mux refused this upload: ${code.replace(/^Mux API error:\s*/i, '').slice(0, 180)}`;
  }
  if (code) return code;

  const raw = error instanceof Error ? error.message : String(error);
  return `Couldn’t start the video upload (${raw}). Check your connection and try again.`;
}

export const createMuxUpload = async (deliveryId: string, fileName: string, fileSize: number) => {
  const { data, error } = await supabase.functions.invoke('create-mux-upload', {
    body: { delivery_id: deliveryId, file_name: fileName, file_size: fileSize },
  });
  if (error) {
    console.error('[FIX-2] create-mux-upload failed', error);
    throw new Error(await describeCreateMuxUploadError(error));
  }
  if (!data?.upload_url || !data?.delivery_file_id) {
    // Defensive: a 200 with a malformed body would otherwise surface
    // as an undefined URL and a cryptic XHR failure a second later.
    throw new Error(
      'The video upload ticket came back incomplete. Try again — if it keeps happening, the Mux configuration needs checking.',
    );
  }
  return data as { delivery_file_id: string; upload_url: string; upload_id: string };
};

export const uploadVideoToMux = (
  uploadUrl: string,
  file: File,
  // POL-1 — now reports a 0…1 fraction (matches resumableUpload)
  // so the queue UI can compute bytesUploaded + ETA in one shape.
  onProgress: (fraction: number) => void,
  // POL-1 — optional cancel signal.  XHR has no native AbortSignal
  // integration, so we wire abort → xhr.abort() manually and reject
  // the outer promise with a distinguishable "aborted" error the
  // caller can pattern-match on.
  signal?: AbortSignal,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Upload aborted'));
      return;
    }
    const xhr = new XMLHttpRequest();
    const abortListener = () => xhr.abort();
    signal?.addEventListener('abort', abortListener);
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    });
    xhr.addEventListener('load', () => {
      signal?.removeEventListener('abort', abortListener);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      // FIX-2 — "Upload failed with status 403" told the studio
      // nothing actionable. Mux direct-upload URLs are short-lived and
      // single-use, which is by far the most common cause.
      if (xhr.status === 401 || xhr.status === 403) {
        reject(
          new Error(
            'The upload link expired before the file finished. Press Retry to get a fresh one.',
          ),
        );
        return;
      }
      reject(
        new Error(
          `Mux rejected the transfer (HTTP ${xhr.status}). Press Retry — if it keeps failing, re-export the file as an H.264 MP4 or MOV.`,
        ),
      );
    });
    xhr.addEventListener('error', () => {
      signal?.removeEventListener('abort', abortListener);
      reject(
        new Error(
          'The connection dropped while sending this video. Press Retry to send it again.',
        ),
      );
    });
    xhr.addEventListener('abort', () => {
      signal?.removeEventListener('abort', abortListener);
      reject(new Error('Upload aborted'));
    });
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

export const getDeliveryPhotoUrl = (storagePath: string): string => {
  const { data } = supabase.storage.from('delivery-photos').getPublicUrl(storagePath);
  return data.publicUrl;
};

export const getSignedDeliveryPhotoUrl = async (storagePath: string): Promise<string> => {
  const { data, error } = await supabase.storage
    .from('delivery-photos')
    .createSignedUrl(storagePath, 3600);
  if (error) throw error;
  return data.signedUrl;
};
